/// <reference path="../types.d.ts" />
import InitialState from '../reducers/initial-state';
import { actions } from '../actions/users';
import { clearAllFilters } from '../actions/cases.action';
import { createReducer } from '@reduxjs/toolkit';
import { LOAD_STATES } from './common';
import { uiActions, commonEnums as enums } from '@codametrix/ui-common';
import { formErrors } from '../stubs/form-errors';
import { logout } from '../actions/ui';
import { jumpContext } from '../actions/contexts.action';
const { FilterTypes } = enums;
/**
 * Same flatten/order as before, but never mutates org objects (RTK/Immer drafts are read-only).
 */ function flattenAndSortOrganizations(root) {
    const orgs = [
        root
    ];
    function recurse(org) {
        (org.children ?? []).forEach((child)=>{
            if (!orgs.some((o)=>o.organizationName === child.organizationName)) {
                orgs.push(child);
            }
            recurse(child);
        });
    }
    recurse(root);
    return orgs.map((o)=>({
            ...o,
            displayName: o.displayName ?? o.organizationName
        })).sort((orgA, orgB)=>orgA.organizationName < orgB.organizationName ? -1 : 1);
}
function formatOrganizations(state, orgDef) {
    return {
        ...state,
        availableOrganizations: flattenAndSortOrganizations(orgDef)
    };
}
function hasEditingPermission(userContext) {
    const activeContextId = userContext.activeContextId;
    const activeContext = userContext.contexts.find((cont)=>{
        return cont.organizationId === activeContextId;
    });
    const activeRoles = userContext.user.roles.find((org)=>{
        return org.tenantId === activeContext?.tenantId;
    });
    // Refactor to make this resource based defined in CDMX-1853
    return activeRoles?.roles.some((role)=>{
        return role.roleName === 'super_admin' || role.roleName === 'administrator';
    }) ?? false;
}
const defaultFilter = [
    {
        key: 'role',
        terms: [
            'true'
        ],
        type: FilterTypes.IN
    }
];
const wipeErrors = (state)=>{
    return {
        ...state,
        formErrors: formErrors.noErrors
    };
};
const reducer = createReducer(InitialState.users, (builder)=>{
    builder.addCase(logout.type, ()=>({
            ...InitialState.users
        }));
    builder.addCase(jumpContext.pending.type, ()=>({
            ...InitialState.users
        }));
    // save.pending and addUser.pending → wipeErrors
    builder.addCase(actions.save.pending, wipeErrors);
    builder.addCase(actions.addUser.pending, wipeErrors);
    builder.addCase(actions.save.rejected, (state, action)=>{
        const error = action.payload;
        return {
            ...state,
            formErrors: error?.errors,
            errors: error?.errors ? Object.keys(error.errors?.fieldErrors).includes('roles') : false
        };
    });
    builder.addCase(actions.addUser.rejected, (state, action)=>{
        const error = action.payload;
        return {
            ...state,
            formErrors: error?.errors,
            errors: error?.errors ? Object.keys(error.errors?.fieldErrors).includes('roles') : false
        };
    });
    builder.addCase(actions.userSelected, (state, action)=>{
        const selectedUser = action.payload;
        const setselectedUser = {
            ...state,
            selectedUser
        };
        return wipeErrors(setselectedUser);
    });
    builder.addCase(actions.updateRole, (state, action)=>({
            ...state,
            selectedUser: action.payload
        }));
    builder.addCase(actions.userList, (state, action)=>{
        const users = action.payload;
        const { sortablePageable, criteriaFilter } = state;
        const users_content = users.content;
        const sp = {
            ...sortablePageable,
            pageableDefinition: {
                ...users,
                items: users_content
            }
        };
        const cFilter = {
            ...criteriaFilter,
            filters: sp.filterableOptions.filters ?? criteriaFilter.filters
        };
        return {
            ...state,
            sortablePageable: sp,
            items: users_content,
            formErrors: InitialState.users.formErrors,
            users: users_content,
            criteriaFilter: cFilter
        };
    });
    builder.addCase(actions.availableOrgs, (state, action)=>formatOrganizations(state, action.payload));
    builder.addCase(actions.availableRoles, (state, action)=>({
            ...state,
            availableRoles: action.payload
        }));
    builder.addCase(actions.addUserDetails, (state, action)=>({
            ...state,
            selectedUser: action.payload
        }));
    builder.addCase(actions.toggleForcePasswordOption, (state, action)=>({
            ...state,
            passwordChangeEnabled: action.payload
        }));
    builder.addCase(actions.togglePasswordChangeModal, (state, action)=>({
            ...state,
            passwordModalOpen: action.payload,
            passwordChangeStatus: LOAD_STATES.initial,
            formErrors: formErrors.noErrors
        }));
    builder.addCase(actions.changeUsersPassword.pending, (state)=>({
            ...state,
            passwordChangeStatus: LOAD_STATES.started
        }));
    builder.addCase(actions.changeUsersPassword.rejected, (state, action)=>{
        const error = action.payload;
        return {
            ...state,
            formErrors: error?.errors ?? formErrors.noErrors,
            passwordChangeStatus: LOAD_STATES.failed
        };
    });
    builder.addCase(actions.changeUsersPassword.fulfilled, (state)=>({
            ...state,
            passwordChangeStatus: LOAD_STATES.done
        }));
    builder.addCase(actions.toggleFilterOn, (state, action)=>({
            ...state,
            filterOn: action.payload
        }));
    builder.addCase(clearAllFilters.type, (state)=>({
            ...state,
            criteriaFilter: {
                ...state.criteriaFilter,
                filters: defaultFilter
            },
            sortablePageable: {
                ...state.sortablePageable,
                filterableOptions: {
                    filters: defaultFilter
                },
                filters: defaultFilter
            }
        }));
    builder.addCase(uiActions.chooseContext.type, (state, action)=>{
        const context = action.payload;
        const editPermission = hasEditingPermission(context);
        let { criteriaFilter } = state;
        if (editPermission) {
            const filterUsersFields = [
                {
                    key: 'role',
                    helpText: 'Hide External Users',
                    required: false,
                    type: 'checkbox',
                    label: 'Hide External Users',
                    value: 'true',
                    filterType: FilterTypes.IN
                },
                {
                    key: 'role',
                    helpText: 'All Users',
                    required: false,
                    type: 'checkbox',
                    label: 'All Users',
                    value: 'false',
                    filterType: FilterTypes.IN
                }
            ];
            const formDefinitionVersion = {
                fieldGroups: [
                    {
                        label: 'Filter Users',
                        fields: filterUsersFields
                    }
                ]
            };
            const versionCriteria = {
                name: 'User Filters',
                key: 'role',
                formDefinition: {
                    ...formDefinitionVersion
                },
                isSelect: true
            };
            criteriaFilter = {
                ...criteriaFilter,
                filterForms: [
                    versionCriteria
                ]
            };
        }
        return {
            ...state,
            editPermission: editPermission,
            criteriaFilter: criteriaFilter
        };
    });
    builder.addCase(actions.toggleShowErrors, (state, action)=>({
            ...state,
            errors: action.payload
        }));
});
export default reducer;
